import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import useInView from '../../utils/useInView';
import AnimatedSection from './AnimatedSection';

interface SectionProps {
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
  textColor?: string;
  padding?: string;
  centered?: boolean;
  children: React.ReactNode;
  id?: string;
  animate?: boolean;
  animationDirection?: 'up' | 'down' | 'left' | 'right' | 'none';
  animationDelay?: number;
  backgroundImage?: string;
  blurredBackground?: boolean;
}

const SectionContainer = styled.section<{
  $backgroundColor?: string;
  $textColor?: string;
  $padding: string;
  $backgroundImage?: string;
  $blurredBackground?: boolean;
}>`
  background-color: ${({ $backgroundColor }) => 
    $backgroundColor || 'var(--bg-primary)'};
  color: ${({ $textColor }) => $textColor || 'var(--text-primary)'};
  padding: ${({ $padding }) => $padding};
  width: 100%;
  position: relative;
  overflow: hidden;
  
  ${props => props.$backgroundImage && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url(${props.$backgroundImage});
      background-size: cover;
      background-position: center;
      z-index: 0;
      ${props.$blurredBackground && `
        filter: blur(8px);
        transform: scale(1.1);
      `}
      opacity: 0.15;
    }
  `}
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div<{ $centered: boolean }>`
  margin-bottom: 3rem;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled(motion.h2)<{ $centered: boolean }>`
  position: relative;
  margin-bottom: 1.5rem;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
    
    ${({ $centered }) => $centered && `
      left: 50%;
      transform: translateX(-50%);
    `}
  }
`;

const SectionSubtitle = styled(motion.p)<{ $centered: boolean }>`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: ${({ $centered }) => $centered ? '0 auto' : '0'};
`;

const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  backgroundColor,
  textColor,
  padding = '5rem 0',
  centered = false,
  children,
  id,
  animate = true,
  animationDirection = 'up',
  animationDelay = 0,
  backgroundImage,
  blurredBackground = false,
}) => {
  const [headerRef, headerInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const sectionContent = (
    <SectionInner>
      {(title || subtitle) && (
        <SectionHeader 
          $centered={centered} 
          ref={headerRef as React.RefObject<HTMLDivElement>}
        >
          {title && (
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              $centered={centered}
            >
              {title}
            </SectionTitle>
          )}
          {subtitle && (
            <SectionSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              $centered={centered}
            >
              {subtitle}
            </SectionSubtitle>
          )}
        </SectionHeader>
      )}
      
      {animate ? (
        <AnimatedSection 
          direction={animationDirection} 
          delay={animationDelay} 
          threshold={0.1}
        >
          {children}
        </AnimatedSection>
      ) : (
        children
      )}
    </SectionInner>
  );

  return (
    <SectionContainer
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $padding={padding}
      $backgroundImage={backgroundImage}
      $blurredBackground={blurredBackground}
      id={id}
    >
      {sectionContent}
    </SectionContainer>
  );
};

export default Section; 